import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  FileText, Search, Plus, CheckCircle2,
  ArrowRight, ChevronRight, Settings,
  Package, Wrench, Building2, X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { useApp } from '../context/AppContext';
import { useTrace } from '../context/TraceContext';
import { useQualityEvents } from '../context/QualityEventsContext';
import TraceLabel from '../components/trace/TraceLabel';
import { formatDate } from '../data/mockData';
import ChangeControlCreateDialog from '../components/quality-events/ChangeControlCreateDialog';
import ReportExportButton from '../components/export/ReportExportButton';
import ReportSection from '../components/export/ReportSection';
import { toast } from 'sonner';

const ChangeControlPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { pidTraceMode } = useTrace();
  const { isReadOnly, currentRole } = useApp();
  const {
    changeControls,
    ccCounts,
    filterChangeControls,
    getChangeControlById,
    updateChangeControl,
    submitChangeControl,
    approveChangeControl,
    rejectChangeControl,
    implementChangeControl,
    closeChangeControl,
    permissions,
    can
  } = useQualityEvents();

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedCC, setSelectedCC] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    const id = searchParams.get('id');
    if (id) {
      const cc = getChangeControlById(id);
      if (cc) {
        setSelectedCC(cc);
      }
    }
  }, [searchParams, getChangeControlById]);

  useEffect(() => {
    if (selectedCC) {
      const updated = getChangeControlById(selectedCC.id);
      if (updated) {
        setSelectedCC(updated);
      }
    }
  }, [changeControls, selectedCC, getChangeControlById]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Draft': return 'bg-slate-100 text-slate-600 border-slate-200';
      case 'Under Review': return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'Pending QA Approval': return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'Approved': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'Rejected': return 'bg-red-50 text-red-700 border-red-200';
      case 'Implementation': return 'bg-cyan-50 text-cyan-700 border-cyan-200';
      case 'Implemented': return 'bg-purple-50 text-purple-700 border-purple-200';
      case 'Closed': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      default: return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'High': return 'bg-red-50 text-red-700 border-red-200';
      case 'Medium': return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'Low': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      default: return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'Equipment': return Settings;
      case 'Utility': return Wrench;
      case 'Process': return Package;
      case 'Supplier': return Building2;
      case 'Facility': return Building2;
      default: return FileText;
    }
  };

  const filteredCCs = useMemo(() => {
    return filterChangeControls({
      status: statusFilter,
      search: searchQuery
    });
  }, [filterChangeControls, statusFilter, searchQuery]);

  const handleSubmit = () => {
    if (!selectedCC) return;
    submitChangeControl(selectedCC.id);
    toast.success('Change control submitted for review');
  };

  const handleApprove = () => {
    if (!selectedCC) return;
    approveChangeControl(selectedCC.id);
    toast.success('Change control approved');
  };

  const handleReject = () => {
    if (!selectedCC || !rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    rejectChangeControl(selectedCC.id, rejectionReason);
    toast.success('Change control rejected');
    setRejectionReason('');
  };

  const handleImplement = () => {
    if (!selectedCC) return;
    implementChangeControl(selectedCC.id);
    toast.success('Change control marked as implemented');
  };

  const handleClose = () => {
    if (!selectedCC) return;
    const evidence = prompt('Enter implementation evidence:');
    if (!evidence) return;
    closeChangeControl(selectedCC.id, evidence);
    toast.success('Change control closed');
  };

  const handleCCCreated = (newCC) => {
    setSelectedCC(newCC);
    navigate(`/change-control?id=${newCC.id}`);
  };

  const CCRow = ({ cc }) => {
    const TypeIcon = getTypeIcon(cc.type);
    return (
      <TableRow 
        className={`cursor-pointer hover:bg-slate-50 ${selectedCC?.id === cc.id ? 'bg-slate-100' : ''}`}
        onClick={() => {
          setSelectedCC(cc);
          navigate(`/change-control?id=${cc.id}`);
        }}
        data-testid={`cc-row-${cc.id}`}
      >
        <TableCell className="font-mono text-xs text-slate-500">{cc.id}</TableCell>
        <TableCell>
          <div className="flex items-center gap-2">
            <TypeIcon className="h-4 w-4 text-slate-400" />
            <div className="max-w-xs">
              <p className="text-sm text-slate-800 truncate font-medium">{cc.title}</p>
              <p className="text-xs text-slate-500">{cc.type}</p>
            </div>
          </div>
        </TableCell>
        <TableCell>
          <Badge className={`${getStatusColor(cc.status)} text-xs`}>{cc.status}</Badge>
        </TableCell>
        <TableCell>
          <Badge className={`${getRiskColor(cc.riskLevel)} text-xs`}>{cc.riskLevel}</Badge>
        </TableCell>
        <TableCell className="text-sm text-slate-600">{formatDate(cc.targetDate)}</TableCell>
        <TableCell className="text-sm text-slate-600">{cc.initiator}</TableCell>
        <TableCell>
          <ChevronRight className="h-4 w-4 text-slate-400" />
        </TableCell>
      </TableRow>
    );
  };

  const CCDetail = ({ cc }) => {
    const TypeIcon = getTypeIcon(cc.type);
    
    return (
      <div className="space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-slate-500 font-mono mb-1">{cc.id}</p>
            <h3 className="text-lg font-medium text-slate-900">{cc.title}</h3>
          </div>
          <div className="flex gap-2">
            <Badge className={`${getRiskColor(cc.riskLevel)}`}>{cc.riskLevel} Risk</Badge>
            <Badge className={`${getStatusColor(cc.status)}`}>{cc.status}</Badge>
          </div>
        </div>

        <Separator className="bg-slate-200" />

        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white rounded-md p-3 border border-slate-200">
            <p className="text-xs text-slate-500 mb-1">Type</p>
            <div className="flex items-center gap-2">
              <TypeIcon className="h-4 w-4 text-slate-500" />
              <p className="text-sm text-slate-800 font-medium">{cc.type}</p>
            </div>
          </div>
          <div className="bg-white rounded-md p-3 border border-slate-200">
            <p className="text-xs text-slate-500 mb-1">Initiator</p>
            <p className="text-sm text-slate-800 font-medium">{cc.initiator}</p>
          </div>
          <div className="bg-white rounded-md p-3 border border-slate-200">
            <p className="text-xs text-slate-500 mb-1">Initiated</p>
            <p className="text-sm text-slate-800">{formatDate(cc.initiatedAt)}</p>
          </div>
          <div className="bg-white rounded-md p-3 border border-slate-200">
            <p className="text-xs text-slate-500 mb-1">Target Date</p>
            <p className="text-sm text-slate-800">{formatDate(cc.targetDate)}</p>
          </div>
        </div>

        {cc.reason && (
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-2 font-medium">Reason for Change</p>
            <p className="text-sm text-slate-700">{cc.reason}</p>
          </div>
        )}

        {cc.impactedSOPs && cc.impactedSOPs.length > 0 && (
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-2 font-medium">Impacted SOPs</p>
            <div className="flex flex-wrap gap-1.5">
              {cc.impactedSOPs.map((sop, idx) => (
                <Badge key={idx} className="bg-slate-100 text-slate-700 border border-slate-200 text-xs">
                  {sop}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {cc.impactedTraining && cc.impactedTraining.length > 0 && (
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-2 font-medium">Training Required</p>
            <div className="flex flex-wrap gap-1.5">
              {cc.impactedTraining.map((trn, idx) => (
                <Badge key={idx} className="bg-purple-50 text-purple-700 border border-purple-200 text-xs">
                  {trn}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {!isReadOnly && cc.status !== 'Closed' && (
          <div className="flex gap-2 pt-2">
            {cc.status === 'Draft' && (
              <Button 
                className="bg-[#fd4816] hover:bg-[#ea380c] text-white flex-1"
                onClick={handleSubmit}
              >
                <ArrowRight className="h-4 w-4 mr-2" />
                Submit for Review
              </Button>
            )}
            {(cc.status === 'Under Review' || cc.status === 'Pending QA Approval') && can('cc:approve') && (
              <>
                <Button 
                  className="bg-emerald-600 hover:bg-emerald-700 text-white flex-1" 
                  onClick={handleApprove}
                  data-testid="approve-cc"
                >
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Approve
                </Button>
                <Button 
                  variant="outline" 
                  className="border-red-200 text-red-600 hover:bg-red-50"
                  onClick={() => {
                    const reason = prompt('Enter rejection reason:');
                    if (reason) {
                      setRejectionReason(reason);
                      handleReject();
                    }
                  }}
                >
                  Reject
                </Button>
              </>
            )}
            {cc.status === 'Approved' && (
              <Button 
                className="bg-[#fd4816] hover:bg-[#ea380c] text-white flex-1"
                onClick={handleImplement}
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Mark Implemented
              </Button>
            )}
            {cc.status === 'Implemented' && can('cc:close') && (
              <Button 
                className="bg-emerald-600 hover:bg-emerald-700 text-white flex-1"
                onClick={handleClose}
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Close
              </Button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="change-control-page">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Change Control
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Risk-based change management with multi-level approval workflow
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className="bg-amber-50 text-amber-700 border border-amber-200">
            {ccCounts.pendingQA} Pending QA
          </Badge>
          <Badge className="bg-red-50 text-red-700 border border-red-200">
            {ccCounts.highRisk} High Risk
          </Badge>
          {/* <ReportExportButton 
            title="Change Control Report"
            subtitle="Risk-based change management with multi-level approval workflow"
          /> */}
          {can('cc:create') && (
            <Button 
              className="bg-[#fd4816] hover:bg-[#ea380c] text-white" 
              onClick={() => setCreateDialogOpen(true)}
              data-testid="new-change-control"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Change Request
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <ReportSection id="cc-filters" title="Filters" description="Search and filter change controls">
            <Card className={`bg-white border border-slate-200 shadow-sm relative ${pidTraceMode ? 'border-cyan-300' : ''}`}>
              <TraceLabel prd="PRD §6.5" req="SM-CC-001" />
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      placeholder="Search change controls..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 bg-white border-slate-200 text-slate-700"
                      data-testid="cc-search"
                    />
                  </div>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-[200px] bg-white border-slate-200 text-slate-700">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      <SelectItem value="all" className="text-slate-700">All Status</SelectItem>
                      <SelectItem value="Draft" className="text-slate-700">Draft</SelectItem>
                      <SelectItem value="Under Review" className="text-slate-700">Under Review</SelectItem>
                      <SelectItem value="Pending QA Approval" className="text-slate-700">Pending QA</SelectItem>
                      <SelectItem value="Approved" className="text-slate-700">Approved</SelectItem>
                      <SelectItem value="Implementation" className="text-slate-700">Implementation</SelectItem>
                      <SelectItem value="Implemented" className="text-slate-700">Implemented</SelectItem>
                      <SelectItem value="Closed" className="text-slate-700">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </ReportSection>

          <ReportSection id="cc-list" title="Change Control List" description="All change control requests">
            <Card className={`bg-white border border-slate-200 shadow-sm relative ${pidTraceMode ? 'border-cyan-300' : ''}`}>
              <Table>
              <TableHeader>
                <TableRow className="border-slate-200 hover:bg-transparent">
                  <TableHead className="text-slate-600">ID</TableHead>
                  <TableHead className="text-slate-600">Change Request</TableHead>
                  <TableHead className="text-slate-600">Status</TableHead>
                  <TableHead className="text-slate-600">Risk</TableHead>
                  <TableHead className="text-slate-600">Target</TableHead>
                  <TableHead className="text-slate-600">Initiator</TableHead>
                  <TableHead className="text-slate-600 w-8"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCCs.map(cc => (
                  <CCRow key={cc.id} cc={cc} />
                ))}
                {filteredCCs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                      {can('cc:create') ? (
                        <div className="space-y-2">
                          <p>No change controls found</p>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCreateDialogOpen(true)}
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Create New Change Control
                          </Button>
                        </div>
                      ) : (
                        <p>No change controls found</p>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
          </ReportSection>
        </div>

        <div className="lg:col-span-1">
          <Card className={`bg-white border border-slate-200 shadow-sm sticky top-20 relative ${pidTraceMode ? 'border-cyan-300' : ''}`}>
            <TraceLabel prd="PRD §6.5" req="SM-QRM-001" />
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold text-slate-800">Change Control Detail</CardTitle>
                {selectedCC && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectedCC(null);
                      navigate('/change-control');
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[calc(100vh-280px)]">
                {selectedCC ? (
                  <CCDetail cc={selectedCC} />
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>Select a change control to view details</p>
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>

      <ChangeControlCreateDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onCreated={handleCCCreated}
      />
    </div>
  );
};

export default ChangeControlPage;
